import { useState, useEffect, useCallback, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useMutation, useQuery, useAction } from "convex/react";
import { api } from "../../../../convex/_generated/api";
import type { Id } from "../../../../convex/_generated/dataModel";
import type { Reference, SharedData, NewAppData, ReferenceType, UrlMetadataSuggestion } from "../../../shared/types/reference";
import { useReferenceData } from "../../../shared/contexts/ReferenceDataContext";
import { toast } from "sonner";
import { extractKeywords } from "../../../shared/utils/textUtils";

interface UseReferenceFormProps {
  existingReference?: Reference | null;
  initialImages?: File[];
}

export function useReferenceForm({ existingReference, initialImages }: UseReferenceFormProps) {
  const location = useLocation();
  const { temporaryData, clearTemporaryData } = useReferenceData();
  const hasProcessedTempData = useRef(false);
  const urlExtractionTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  // Form state
  const [title, setTitle] = useState("");
  const [type, setType] = useState<ReferenceType>("screenshot");
  const [description, setDescription] = useState("");
  const [sourceURL, setSourceURL] = useState("");
  const [category, setCategory] = useState("");
  const [imageFiles, setImageFiles] = useState<File[]>(initialImages || []);
  const [currentTags, setCurrentTags] = useState<Array<{ _id: Id<"tags">, name: string }>>([]);
  const [tagInput, setTagInput] = useState("");
  const [suggestedTags, setSuggestedTags] = useState<string[]>([]);
  const [selectedAppId, setSelectedAppId] = useState<Id<"apps"> | "new" | null>(null);
  const [newApp, setNewApp] = useState<NewAppData>({ name: "", url: "", description: "", category: "" });
  const [creatingApp, setCreatingApp] = useState(false);
  const [isPublic, setIsPublic] = useState(false);

  // URL metadata extraction state
  const [isExtractingMetadata, setIsExtractingMetadata] = useState(false);
  const [metadataSuggestions, setMetadataSuggestions] = useState<UrlMetadataSuggestion[]>([]);
  const [extractedThumbnail, setExtractedThumbnail] = useState<string | null>(null);
  const [metadataError, setMetadataError] = useState<string | null>(null);

  // Convex mutations and queries
  const createReference = useMutation(api.references.createReference);
  const updateReference = useMutation(api.references.updateReference);
  const generateUploadUrl = useMutation(api.references.generateUploadUrl);
  const addTagToRef = useMutation(api.tags.addTagToReference);
  const removeTagFromRef = useMutation(api.tags.removeTagFromReference);
  const createApp = useMutation(api.apps.createApp);
  const extractUrlMetadata = useAction(api.urlMetadata.extractUrlMetadata);
  const allUserTags = useQuery(api.tags.getAllUserTags) || [];
  const allApps = useQuery(api.apps.listApps) || [];

  // Initialize form with existing reference data
  useEffect(() => {
    if (existingReference) {
      setTitle(existingReference.title || "");
      setType(existingReference.type || "screenshot");
      setDescription(existingReference.description || "");
      setSourceURL(existingReference.sourceURL || "");
      setCategory(existingReference.category || "");
      setCurrentTags(existingReference.tags || []);
      setSelectedAppId(existingReference.appId || null);
      setIsPublic(existingReference.isPublic || false);
    }
  }, [existingReference]);

  // Handle shared data from location state
  useEffect(() => {
    const sharedData = location.state as SharedData | null;
    if (sharedData && !existingReference) {
      if (sharedData.title) setTitle(sharedData.title);
      if (sharedData.text) setDescription(sharedData.text);
      if (sharedData.url) setSourceURL(sharedData.url);
      if (sharedData.files && sharedData.files.length > 0) {
        setImageFiles(sharedData.files);
      }
    }
  }, [location.state, existingReference]);

  // Handle temporary data from context (paste, drag-drop, web-share-target)
  useEffect(() => {
    if (temporaryData && !hasProcessedTempData.current) {
      console.log('[Form Init] Processing temporary data:', {
        hasImageBlob: !!temporaryData.imageBlob,
        hasTitle: !!temporaryData.title,
        hasSourceURL: !!temporaryData.sourceURL,
        source: temporaryData.source
      });
      
      hasProcessedTempData.current = true;
      
      // Convert blob to file and update form
      if (temporaryData.imageBlob) {
        console.log('[Form Init] Setting image file from blob');
        setImageFiles([temporaryData.imageBlob]);
      }
      
      // Apply any additional data
      if (temporaryData.title) {
        console.log('[Form Init] Setting title:', temporaryData.title);
        setTitle(temporaryData.title);
      }
      if (temporaryData.description) {
        console.log('[Form Init] Setting description:', temporaryData.description);
        setDescription(temporaryData.description);
      }
      if (temporaryData.sourceURL) {
        console.log('[Form Init] Setting source URL:', temporaryData.sourceURL);
        setSourceURL(temporaryData.sourceURL);
      }
      if (temporaryData.category) {
        console.log('[Form Init] Setting category:', temporaryData.category);
        setCategory(temporaryData.category);
      }
      
      // Show success message based on source and content type
      let message = 'Content loaded successfully!';
      if (temporaryData.source === 'paste') {
        if (temporaryData.imageBlob) {
          message = 'Image pasted successfully!';
        } else if (temporaryData.sourceURL) {
          message = 'URL pasted successfully! Extracting metadata...';
        }
      } else if (temporaryData.source === 'drag-drop') {
        message = 'Image dropped successfully!';
      } else if (temporaryData.source === 'web-share-target') {
        message = 'Shared content loaded successfully!';
      }
      
      console.log('[Form Init] Showing success message:', message);
      toast.success(message);
      
      // Clear temporary data
      console.log('[Form Init] Clearing temporary data');
      clearTemporaryData();
    }
  }, [temporaryData, clearTemporaryData]);

  // Auto-generate suggested tags when title or description changes
  useEffect(() => {
    const text = `${title} ${description}`.trim();
    if (text) {
      const keywords = extractKeywords(text);
      setSuggestedTags(keywords.slice(0, 5)); // Limit to 5 suggestions
    } else {
      setSuggestedTags([]);
    }
  }, [title, description]);

  // Debounced URL metadata extraction
  useEffect(() => {
    console.log('[URL Extraction Trigger] Effect triggered:', { 
      sourceURL, 
      hasExistingReference: !!existingReference 
    });
    
    // Clear previous timeout
    if (urlExtractionTimeoutRef.current) {
      console.log('[URL Extraction Trigger] Clearing previous timeout');
      clearTimeout(urlExtractionTimeoutRef.current);
    }

    // Only extract metadata if we have a URL and it's not for an existing reference
    if (sourceURL && !existingReference) {
      console.log('[URL Extraction Trigger] Conditions met, validating URL...');
      
      // Validate URL format before attempting extraction
      try {
        const url = new URL(sourceURL);
        if (['http:', 'https:'].includes(url.protocol)) {
          console.log('[URL Extraction Trigger] URL valid, scheduling extraction in 1 second...');
          // Debounce the extraction to avoid excessive API calls
          urlExtractionTimeoutRef.current = setTimeout(() => {
            console.log('[URL Extraction Trigger] Timeout fired, starting extraction');
            void extractMetadataFromUrl(sourceURL);
          }, 1000); // 1 second delay
        } else {
          console.log('[URL Extraction Trigger] Invalid URL protocol:', url.protocol);
          clearMetadataState();
        }
      } catch (error) {
        // Invalid URL, clear any existing metadata
        console.log('[URL Extraction Trigger] URL validation failed:', error);
        clearMetadataState();
      }
    } else {
      // Clear metadata state if no URL
      console.log('[URL Extraction Trigger] Conditions not met, clearing metadata state');
      clearMetadataState();
    }

    // Cleanup timeout on unmount
    return () => {
      if (urlExtractionTimeoutRef.current) {
        console.log('[URL Extraction Trigger] Cleaning up timeout on unmount');
        clearTimeout(urlExtractionTimeoutRef.current);
      }
    };
  }, [sourceURL, existingReference]);

  const extractMetadataFromUrl = useCallback(async (url: string) => {
    console.log(`[Frontend] Starting URL metadata extraction for: ${url}`);
    
    if (isExtractingMetadata) {
      console.log(`[Frontend] Metadata extraction already in progress, skipping`);
      return; // Prevent concurrent extractions
    }

    console.log(`[Frontend] Setting extraction state to loading...`);
    setIsExtractingMetadata(true);
    setMetadataError(null);

    const startTime = Date.now();

    try {
      console.log(`[Frontend] Calling backend extractUrlMetadata action...`);
      const result = await extractUrlMetadata({ url });
      
      const duration = Date.now() - startTime;
      console.log(`[Frontend] Backend call completed in ${duration}ms`);
      console.log(`[Frontend] Backend result:`, {
        success: result.success,
        hasTitle: !!result.title,
        hasDescription: !!result.description,
        hasSiteName: !!result.siteName,
        hasTags: !!result.tags?.length,
        hasThumbnail: !!result.thumbnail,
        error: result.error
      });

      if (result.success) {
        console.log(`[Frontend] Processing successful metadata extraction...`);
        const suggestions: UrlMetadataSuggestion[] = [];

        // Create suggestions for each extracted field
        if (result.title && result.title !== title) {
          console.log(`[Frontend] Adding title suggestion: "${result.title}"`);
          suggestions.push({
            field: 'title',
            value: result.title,
            confidence: 'high',
            applied: false
          });
        } else if (result.title) {
          console.log(`[Frontend] Title found but matches current form value, skipping suggestion`);
        } else {
          console.log(`[Frontend] No title found in response`);
        }

        if (result.description && result.description !== description) {
          console.log(`[Frontend] Adding description suggestion: "${result.description.substring(0, 50)}..."`);
          suggestions.push({
            field: 'description',
            value: result.description,
            confidence: 'medium',
            applied: false
          });
        } else if (result.description) {
          console.log(`[Frontend] Description found but matches current form value, skipping suggestion`);
        } else {
          console.log(`[Frontend] No description found in response`);
        }

        if (result.siteName && result.siteName !== category) {
          console.log(`[Frontend] Adding category suggestion: "${result.siteName}"`);
          suggestions.push({
            field: 'category',
            value: result.siteName,
            confidence: 'medium',
            applied: false
          });
        } else if (result.siteName) {
          console.log(`[Frontend] Site name found but matches current category, skipping suggestion`);
        } else {
          console.log(`[Frontend] No site name found in response`);
        }

        if (result.tags && result.tags.length > 0) {
          console.log(`[Frontend] Processing ${result.tags.length} tags: ${result.tags.join(', ')}`);
          const newTags = result.tags.filter(tag => 
            !currentTags.some(existingTag => 
              existingTag.name.toLowerCase() === tag.toLowerCase()
            )
          );
          
          if (newTags.length > 0) {
            console.log(`[Frontend] Adding ${newTags.length} new tags: ${newTags.join(', ')}`);
            suggestions.push({
              field: 'tags',
              value: newTags,
              confidence: 'medium',
              applied: false
            });
          } else {
            console.log(`[Frontend] All tags already exist in current form, skipping suggestion`);
          }
        } else {
          console.log(`[Frontend] No tags found in response`);
        }

        if (result.thumbnail) {
          console.log(`[Frontend] Adding thumbnail suggestion: ${result.thumbnail}`);
          suggestions.push({
            field: 'thumbnail',
            value: result.thumbnail,
            confidence: 'high',
            applied: false
          });
          setExtractedThumbnail(result.thumbnail);
        } else {
          console.log(`[Frontend] No thumbnail found in response`);
        }

        console.log(`[Frontend] Setting ${suggestions.length} metadata suggestions`);
        setMetadataSuggestions(suggestions);

        if (suggestions.length > 0) {
          console.log(`[Frontend] Showing success toast for ${suggestions.length} suggestions`);
          toast.success(`Found ${suggestions.length} metadata suggestions from URL`);
        } else {
          console.log(`[Frontend] No suggestions created, showing info message`);
          toast.info('URL metadata extracted but no new suggestions to apply');
        }
      } else {
        console.error(`[Frontend] Metadata extraction failed:`, result.error);
        setMetadataError(result.error || 'Failed to extract metadata');
        toast.error(result.error || 'Failed to extract URL metadata');
      }
    } catch (error) {
      const duration = Date.now() - startTime;
      console.error(`[Frontend] Error during metadata extraction after ${duration}ms:`, error);
      
      const errorMessage = error instanceof Error ? error.message : 'Failed to extract URL metadata';
      console.error(`[Frontend] Setting error message: ${errorMessage}`);
      setMetadataError(errorMessage);
      toast.error(errorMessage);
    } finally {
      console.log(`[Frontend] Clearing extraction loading state`);
      setIsExtractingMetadata(false);
    }
  }, [isExtractingMetadata, title, description, category, currentTags, extractUrlMetadata]);

  const clearMetadataState = useCallback(() => {
    setMetadataSuggestions([]);
    setExtractedThumbnail(null);
    setMetadataError(null);
    setIsExtractingMetadata(false);
  }, []);

  const applyMetadataSuggestion = useCallback((index: number) => {
    const suggestion = metadataSuggestions[index];
    if (!suggestion || suggestion.applied) {
      console.log('[Suggestion Apply] Skipping invalid or already applied suggestion:', { index, suggestion });
      return;
    }

    console.log('[Suggestion Apply] Applying suggestion:', { 
      index, 
      field: suggestion.field, 
      value: suggestion.value,
      confidence: suggestion.confidence 
    });

    switch (suggestion.field) {
      case 'title':
        setTitle(suggestion.value as string);
        console.log('[Suggestion Apply] Applied title suggestion');
        break;
      case 'description':
        setDescription(suggestion.value as string);
        console.log('[Suggestion Apply] Applied description suggestion');
        break;
      case 'category':
        setCategory(suggestion.value as string);
        console.log('[Suggestion Apply] Applied category suggestion');
        break;
      case 'tags': {
        const newTags = (suggestion.value as string[]).map(tagName => ({
          _id: undefined as unknown as Id<"tags">,
          name: tagName
        }));
        setCurrentTags(prev => [...prev, ...newTags]);
        console.log('[Suggestion Apply] Applied tags suggestion:', newTags.map(t => t.name));
        break;
      }
      case 'thumbnail': {
        setExtractedThumbnail(suggestion.value as string);
        console.log('[Suggestion Apply] Applied thumbnail suggestion:', suggestion.value);
        break;
      }
    }

    // Mark suggestion as applied
    setMetadataSuggestions(prev => 
      prev.map((s, i) => 
        i === index ? { ...s, applied: true } : s
      )
    );

    console.log('[Suggestion Apply] Marked suggestion as applied and showing success toast');
    toast.success(`Applied ${suggestion.field} suggestion`);
  }, [metadataSuggestions]);

  const rejectMetadataSuggestion = useCallback((index: number) => {
    const suggestion = metadataSuggestions[index];
    console.log('[Suggestion Reject] Rejecting suggestion:', { 
      index, 
      field: suggestion?.field, 
      value: suggestion?.value 
    });
    
    setMetadataSuggestions(prev => prev.filter((_, i) => i !== index));
    console.log('[Suggestion Reject] Suggestion removed from list');
  }, []);

  const applyAllMetadataSuggestions = useCallback(() => {
    const unappliedSuggestions = metadataSuggestions.filter(s => !s.applied);
    console.log('[Apply All] Applying all unapplied suggestions:', unappliedSuggestions.length);
    
    if (unappliedSuggestions.length === 0) {
      console.log('[Apply All] No unapplied suggestions found');
      return;
    }
    
    // Apply all suggestions at once to avoid index shifting issues
    unappliedSuggestions.forEach((suggestion, index) => {
      console.log(`[Apply All] Processing suggestion ${index + 1}/${unappliedSuggestions.length}:`, {
        field: suggestion.field,
        value: suggestion.value,
        confidence: suggestion.confidence
      });

      switch (suggestion.field) {
        case 'title':
          setTitle(suggestion.value as string);
          console.log('[Apply All] Applied title suggestion');
          break;
        case 'description':
          setDescription(suggestion.value as string);
          console.log('[Apply All] Applied description suggestion');
          break;
        case 'category':
          setCategory(suggestion.value as string);
          console.log('[Apply All] Applied category suggestion');
          break;
        case 'tags': {
          const newTags = (suggestion.value as string[]).map(tagName => ({
            _id: undefined as unknown as Id<"tags">,
            name: tagName
          }));
          setCurrentTags(prev => [...prev, ...newTags]);
          console.log('[Apply All] Applied tags suggestion:', newTags.map(t => t.name));
          break;
        }
        case 'thumbnail': {
          setExtractedThumbnail(suggestion.value as string);
          console.log('[Apply All] Applied thumbnail suggestion:', suggestion.value);
          break;
        }
      }
    });
    
    // Mark all suggestions as applied in one state update
    setMetadataSuggestions(prev => 
      prev.map(s => ({ ...s, applied: true }))
    );
    
    console.log('[Apply All] Completed applying all suggestions and marked them as applied');
    toast.success(`Applied ${unappliedSuggestions.length} suggestions`);
  }, [metadataSuggestions]);

  const resetForm = useCallback(() => {
    setTitle("");
    setType("screenshot");
    setDescription("");
    setSourceURL("");
    setCategory("");
    setImageFiles([]);
    setCurrentTags([]);
    setTagInput("");
    setSuggestedTags([]);
    setSelectedAppId(null);
    setNewApp({ name: "", url: "", description: "", category: "" });
    setCreatingApp(false);
    hasProcessedTempData.current = false;
    
    // Clear URL metadata state
    setMetadataSuggestions([]);
    setExtractedThumbnail(null);
    setMetadataError(null);
    setIsExtractingMetadata(false);
  }, []);

  const handleAddTag = useCallback(async () => {
    const trimmedTagInput = tagInput.trim();
    if (!trimmedTagInput) return;

    if (existingReference) {
      // For existing references, add tag via mutation
      try {
        await addTagToRef({
          referenceId: existingReference._id,
          tagName: trimmedTagInput
        });
        toast.success(`Tag "${trimmedTagInput}" added.`);
        setTagInput("");
      } catch (error) {
        console.error("Error adding tag:", error);
        toast.error("Failed to add tag.");
      }
    } else {
      // For new references, add to local state
      const tagExists = currentTags.some(tag => tag.name.toLowerCase() === trimmedTagInput.toLowerCase());
      if (!tagExists) {
        setCurrentTags(prev => [...prev, { 
          _id: undefined as unknown as Id<"tags">, 
          name: trimmedTagInput 
        }]);
      }
      setTagInput("");
    }
  }, [tagInput, existingReference, addTagToRef, currentTags]);

  const handleRemoveTag = useCallback(async (tagId: Id<"tags">) => {
    if (existingReference) {
      // For existing references, remove tag via mutation
      try {
        await removeTagFromRef({
          referenceId: existingReference._id,
          tagId
        });
        toast.success("Tag removed.");
      } catch (error) {
        console.error("Error removing tag:", error);
        toast.error("Failed to remove tag.");
      }
    } else {
      // For new references, remove from local state
      setCurrentTags(prev => prev.filter(tag => tag._id !== tagId));
    }
  }, [existingReference, removeTagFromRef]);

  const handleAddSuggestedTag = useCallback((tagName: string) => {
    const tagExists = currentTags.some(tag => tag.name.toLowerCase() === tagName.toLowerCase());
    if (!tagExists) {
      setCurrentTags(prev => [...prev, { 
        _id: undefined as unknown as Id<"tags">, 
        name: tagName 
      }]);
    }
  }, [currentTags]);

  const handleCreateApp = useCallback(async () => {
    if (!newApp.name.trim()) {
      toast.error("App name is required");
      return;
    }
    try {
      const appId = await createApp({
        name: newApp.name.trim(),
        url: newApp.url.trim() || undefined,
        description: newApp.description.trim() || undefined,
        category: newApp.category.trim() || undefined,
      });
      toast.success("App created!");
      setSelectedAppId(appId);
      setCreatingApp(false);
      setNewApp({ name: "", url: "", description: "", category: "" });
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : "Failed to create app";
      toast.error(errorMessage);
    }
  }, [newApp, createApp]);

  return {
    // Form state
    title, setTitle,
    type, setType,
    description, setDescription,
    sourceURL, setSourceURL,
    category, setCategory,
    imageFiles, setImageFiles,
    currentTags, setCurrentTags,
    tagInput, setTagInput,
    suggestedTags,
    selectedAppId, setSelectedAppId,
    newApp, setNewApp,
    creatingApp, setCreatingApp,
    isPublic, setIsPublic,
    
    // Data
    allUserTags,
    allApps,
    
    // Actions
    resetForm,
    handleAddTag,
    handleRemoveTag,
    handleAddSuggestedTag,
    handleCreateApp,
    
    // Mutations for form submission
    createReference,
    updateReference,
    generateUploadUrl,
    addTagToRef,
    
    // URL metadata extraction
    isExtractingMetadata,
    metadataSuggestions,
    extractedThumbnail,
    metadataError,
    applyMetadataSuggestion,
    rejectMetadataSuggestion,
    applyAllMetadataSuggestions,
    
    // Testing helpers (only available in development)
    ...(process.env.NODE_ENV === 'development' || process.env.NODE_ENV === 'test' ? {
      _setMetadataSuggestions: setMetadataSuggestions
    } : {})
  };
} 